import { QuizMessage as QuizMessageType } from '@/store/types'
import { Store } from '@/store'
import AssistantMessage from '@/components/AssistantMessage'
import MarkdownRenderer from '@/components/MarkdownRenderer'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { analytics } from '@/lib/analytics'

interface QuizMessageProps {
	message: QuizMessageType
	chatId?: string
}

const QuizMessage = ({ message, chatId }: QuizMessageProps) => {
	const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({})
	const [showResults, setShowResults] = useState(false)

	useEffect(() => {
		analytics.quizStarted()
	}, [message.content.quizTitle])

	const handleOptionSelect = (questionIndex: number, option: string) => {
		setSelectedAnswers(prev => ({
			...prev,
			[questionIndex]: option,
		}))
	}

	const handleSubmitQuiz = () => {
		try {
			setShowResults(true)

			Sentry.captureEvent({
				message: 'Quiz submitted',
				level: 'info',
				tags: {
					type: 'engagement',
					id: 'quiz-submission',
					chatId,
					questionsCount: message.content.questions.length,
				},
			})
		} catch (error) {
			Sentry.captureException(error, {
				tags: {
					type: 'ui-interaction',
					id: 'quiz-submit',
				},
			})
		}
	}

	const allAnswered = message.content.questions.every(
		(_q, idx) => selectedAnswers[idx]
	)

	const correctCount = message.content.questions.filter(
		(q, idx) => selectedAnswers[idx] === q.correctAnswer
	).length

	return (
		<div className='rounded-lg bg-opacity-10 bg-green-700 p-4 text-left'>
			<AssistantMessage>
				<div className='space-y-4'>
					<div>
						<h3 className='text-base font-semibold text-primary-foreground mb-1'>
							{message.content.quizTitle}
						</h3>
						<p className='text-sm text-primary-foreground/80'>
							{message.content.quizDescription}
						</p>
					</div>

					<div className='space-y-4'>
						{message.content.questions.map((question, qIdx) => (
							<div key={qIdx} className='bg-opacity-5 bg-white p-3 rounded-lg'>
								<p className='text-sm font-medium text-primary-foreground mb-2'>
									<span className='text-[#249F7E] font-semibold'>Q{qIdx + 1}.</span>
									{' '}
									<MarkdownRenderer content={question.question} />
								</p>

								<div className='space-y-1.5'>
									{question.options.map((option, oIdx) => {
										const isSelected = selectedAnswers[qIdx] === option
										const isCorrect = option === question.correctAnswer
										const showCorrect = showResults && isCorrect
										const showIncorrect = showResults && isSelected && !isCorrect

										return (
											<button
												key={oIdx}
												onClick={() => !showResults &&
													handleOptionSelect(qIdx, option)
												}
												disabled={showResults}
												className={`w-full text-left px-3 py-2 rounded-lg text-sm
													transition-all duration-200
													${showCorrect
														? 'bg-green-600/50 border border-green-500 text-white'
														: showIncorrect
															? 'bg-red-600/50 border border-red-500 text-white'
															: isSelected
																? 'bg-[#249F7E]/50 border border-[#249F7E] text-white'
																: 'bg-white/10 border border-white/20 text-primary-foreground hover:bg-white/20'
													} ${showResults ? 'cursor-default' : 'cursor-pointer'}
												`}
											>
												<MarkdownRenderer content={option} />
											</button>
										)
									})}
								</div>
							</div>
						))}
					</div>

					{showResults && (
						<div className='bg-blue-600/20 border border-blue-500/50 rounded-lg p-3'>
							<p className='text-sm font-semibold text-primary-foreground'>
								Score: {correctCount} / {message.content.questions.length}
							</p>
						</div>
					)}

					{!showResults && (
						<Button
							onClick={handleSubmitQuiz}
							disabled={!allAnswered}
							className='w-full bg-[#249F7E] hover:bg-[#1a7a62]
								text-white font-medium py-2 rounded-lg disabled:opacity-50'
						>
							Submit Quiz
						</Button>
					)}
				</div>
			</AssistantMessage>
		</div>
	)
}

export default QuizMessage
